import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function externalUrl(url) {
  if (!url) return '#';
  const trimmed = String(url).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export function compressImage(file, maxDim = 1280, quality = 0.8) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
      return;
    }
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Invalid image')); };
    img.src = url;
  });
}

export function formatCurrency(amount, currency = 'NGN') {
  // Handle undefined, null, or invalid values
  if (amount === undefined || amount === null || isNaN(amount)) {
    return currency === 'NGN' ? '₦0.00' : currency === 'USD' ? '$0.00' : '¥0.00';
  }
  
  if (currency === 'NGN') {
    return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else if (currency === 'RMB' || currency === 'CNY') {
    return `¥${amount.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } else if (currency === 'USD') {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `${currency} ${amount.toLocaleString()}`;
}

export function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function getStatusColor(status) {
  const statusColors = {
    // Procurement
    draft: 'bg-slate-100 text-slate-700',
    quoted: 'bg-blue-100 text-blue-700',
    paid: 'bg-green-100 text-green-700',
    shipped: 'bg-purple-100 text-purple-700',
    delivered: 'bg-green-100 text-green-700',
    warehouse: 'bg-teal-100 text-teal-700',
    
    // Tracking
    submitted: 'bg-blue-100 text-blue-700',
    arrived_warehouse: 'bg-green-100 text-green-700',
    
    // Warehouse
    available: 'bg-green-100 text-green-700',
    consolidated: 'bg-purple-100 text-purple-700',
    shipped_out: 'bg-orange-100 text-orange-700',
    
    // Payment
    pending: 'bg-yellow-100 text-yellow-700',
    success: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    
    // Shipping
    requested: 'bg-blue-100 text-blue-700',
    in_transit: 'bg-purple-100 text-purple-700',
    pending_payment: 'bg-yellow-100 text-yellow-700',
  };
  
  return statusColors[status] || 'bg-slate-100 text-slate-700';
}

export function getShippingMethodLabel(method, customNames = null, customDurations = null) {
  const labels = {
    express: 'Express Air (2-3 days)',
    air_normal: 'Air Normal (7-10 days)',
    air_special: 'Air Special (10-14 days)',
    sea_normal: 'Sea Normal (60-70 days)',
    sea_special: 'Sea Special (60-80 days)',
  };
  if (customNames && customNames[method]) {
    const dur = customDurations && customDurations[method];
    return dur ? `${customNames[method]} (${dur})` : customNames[method];
  }
  return labels[method] || method;
}

// Debounce function for search
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}
